import axios from 'axios';
import { CONFIG } from '../config.js';

/**
 * Adapter HTTP.
 *
 * Il resto dell'applicazione non conosce Axios: parla solo con un oggetto che
 * espone `get(path)`. Sostituire Axios con `fetch` (o mockarlo nei test)
 * significa cambiare solo questo file.
 */

/** Piccola utility di attesa, usata dal backoff. */
const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Un errore è "ritentabile" se non è una risposta 4xx del server:
 * timeout, connessione caduta o errore 5xx possono rientrare al tentativo dopo.
 *
 * @param {unknown} error
 * @returns {boolean}
 */
export function isRetryableError(error) {
  const status = error?.response?.status;
  if (typeof status === 'number') return status >= 500;
  return true; // nessuna risposta => problema di rete/timeout
}

/**
 * Crea un client HTTP con retry e backoff esponenziale.
 *
 * @param {object} [options]
 * @param {string} [options.baseUrl]
 * @param {number} [options.timeout]
 * @param {number} [options.maxRetries]
 * @param {number} [options.retryDelay]
 * @param {object} [options.adapter] istanza compatibile Axios (utile nei test)
 * @returns {{ get: (path: string, config?: object) => Promise<any> }}
 */
export function createHttpClient({
  baseUrl = CONFIG.API_BASE_URL,
  timeout = CONFIG.REQUEST_TIMEOUT,
  maxRetries = CONFIG.MAX_RETRIES,
  retryDelay = CONFIG.RETRY_DELAY,
  adapter,
} = {}) {
  const instance = adapter ?? axios.create({ baseURL: baseUrl, timeout });

  async function get(path, config = {}) {
    let lastError;

    for (let attempt = 0; attempt <= maxRetries; attempt += 1) {
      try {
        const response = await instance.get(path, config);
        return response.data;
      } catch (error) {
        lastError = error;
        if (attempt === maxRetries || !isRetryableError(error)) break;
        await wait(retryDelay * 2 ** attempt);
      }
    }

    throw lastError;
  }

  return { get };
}
